import React, { useEffect, useState } from "react";
import { Settings as SettingsIcon, Save, KeyRound, Bot, Link2, MessageCircle, Mail, Palette } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";

const inputCls = "h-10 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm outline-none focus:border-brand-400 focus:bg-white focus:ring-2 focus:ring-brand-200";
const smallNote = "mt-2 text-xs text-slate-500";

const defaultSettings = {
  appName: "AWebBee GMB Automation Manager",
  primaryColor: "#291d89",
  secondaryColor: "#4e67eb",
  google: { clientId: "", clientSecret: "", redirectUri: "https://gmb.awebbee.com/api/google/callback" },
  ai: { provider: "openrouter", openRouterApiKey: "", model: "openrouter/free", openaiApiKey: "", geminiApiKey: "" },
  whatsapp: { businessAccountId: "", phoneNumberId: "", accessToken: "" },
  smtp: { host: "", port: "", user: "", pass: "" },
};

function mergeDefaults(data) {
  return {
    ...defaultSettings,
    ...(data || {}),
    google: { ...defaultSettings.google, ...(data?.google || {}) },
    ai: { ...defaultSettings.ai, ...(data?.ai || {}) },
    whatsapp: { ...defaultSettings.whatsapp, ...(data?.whatsapp || {}) },
    smtp: { ...defaultSettings.smtp, ...(data?.smtp || {}) },
  };
}

export default function AdminSettings() {
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/app-settings").then(({ data }) => setS(mergeDefaults(data)));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/app-settings", s);
      setS(mergeDefaults(data));
      toast.success("API settings saved. Google and AI will work without editing .env now.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!s) return <div className="space-y-6"><PageHeader title="Settings" /></div>;

  return (
    <div className="space-y-6">
      <PageHeader title="App Settings" subtitle="Add Google OAuth, OpenRouter AI, WhatsApp and branding keys from dashboard" icon={SettingsIcon}
        actions={<Button onClick={save} disabled={saving} className="rounded-xl bg-brand-700 text-white hover:bg-brand-800" data-testid="save-app-settings"><Save className="mr-1 h-4 w-4" /> {saving ? "Saving..." : "Save Settings"}</Button>} />

      <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-800">
        <b>Use this page for API keys.</b> After saving, go to <b>Client Dashboard → Google Connection</b> and click <b>Connect Google Account</b>. No need to edit Hostinger .env for Google/AI keys.
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <div className="card-surface p-6 lg:col-span-2">
          <h3 className="mb-4 flex items-center gap-2 font-heading text-base font-semibold text-slate-800"><Link2 className="h-5 w-5 text-brand-600" /> Google OAuth / Google Business Profile API</h3>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">GOOGLE_CLIENT_ID</label>
              <input className={inputCls} value={s.google?.clientId || ""} onChange={(e) => setS({ ...s, google: { ...s.google, clientId: e.target.value } })} placeholder="Paste Google OAuth Client ID" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">GOOGLE_CLIENT_SECRET</label>
              <input className={inputCls} type="password" value={s.google?.clientSecret || ""} onChange={(e) => setS({ ...s, google: { ...s.google, clientSecret: e.target.value } })} placeholder="Paste Google OAuth Client Secret" />
            </div>
            <div className="lg:col-span-2">
              <label className="mb-1 block text-xs font-medium text-slate-600">GOOGLE_REDIRECT_URI</label>
              <input className={inputCls} value={s.google?.redirectUri || ""} onChange={(e) => setS({ ...s, google: { ...s.google, redirectUri: e.target.value } })} placeholder="https://gmb.awebbee.com/api/google/callback" />
              <p className={smallNote}>Add the same Redirect URI in Google Cloud Console → OAuth client → Authorized redirect URIs.</p>
            </div>
          </div>
        </div>

        <div className="card-surface p-6 lg:col-span-2">
          <h3 className="mb-4 flex items-center gap-2 font-heading text-base font-semibold text-slate-800"><Bot className="h-5 w-5 text-brand-600" /> AI Provider / OpenRouter</h3>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Provider</label>
              <select className={inputCls} value={s.ai?.provider || "openrouter"} onChange={(e) => setS({ ...s, ai: { ...s.ai, provider: e.target.value } })}>
                <option value="openrouter">OpenRouter</option>
                <option value="openai">OpenAI</option>
                <option value="gemini">Gemini key saved only</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">AI_MODEL</label>
              <input className={inputCls} value={s.ai?.model || "openrouter/free"} onChange={(e) => setS({ ...s, ai: { ...s.ai, model: e.target.value } })} placeholder="openrouter/free" />
              <p className={smallNote}>For free OpenRouter use <b>openrouter/free</b>. You can also use a paid model later.</p>
            </div>
            <div className="lg:col-span-2">
              <label className="mb-1 block text-xs font-medium text-slate-600">OPENROUTER_API_KEY</label>
              <input className={inputCls} type="password" value={s.ai?.openRouterApiKey || ""} onChange={(e) => setS({ ...s, ai: { ...s.ai, openRouterApiKey: e.target.value } })} placeholder="sk-or-v1-..." />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">OPENAI_API_KEY optional</label>
              <input className={inputCls} type="password" value={s.ai?.openaiApiKey || ""} onChange={(e) => setS({ ...s, ai: { ...s.ai, openaiApiKey: e.target.value } })} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">GEMINI_API_KEY optional</label>
              <input className={inputCls} type="password" value={s.ai?.geminiApiKey || ""} onChange={(e) => setS({ ...s, ai: { ...s.ai, geminiApiKey: e.target.value } })} />
            </div>
          </div>
        </div>

        <div className="card-surface p-6">
          <h3 className="mb-4 flex items-center gap-2 font-heading text-base font-semibold text-slate-800"><Palette className="h-5 w-5 text-brand-600" /> Branding</h3>
          <div className="space-y-3">
            <div><label className="mb-1 block text-xs font-medium text-slate-600">App Name</label><input className={inputCls} value={s.appName || ""} onChange={(e) => setS({ ...s, appName: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="mb-1 block text-xs font-medium text-slate-600">Primary Color</label><input type="color" className="h-10 w-full rounded-xl border border-slate-200" value={s.primaryColor || "#291d89"} onChange={(e) => setS({ ...s, primaryColor: e.target.value })} /></div>
              <div><label className="mb-1 block text-xs font-medium text-slate-600">Secondary Color</label><input type="color" className="h-10 w-full rounded-xl border border-slate-200" value={s.secondaryColor || "#4e67eb"} onChange={(e) => setS({ ...s, secondaryColor: e.target.value })} /></div>
            </div>
          </div>
        </div>

        <div className="card-surface p-6">
          <h3 className="mb-4 flex items-center gap-2 font-heading text-base font-semibold text-slate-800"><MessageCircle className="h-5 w-5 text-brand-600" /> Global WhatsApp Cloud API</h3>
          <div className="space-y-3">
            <div><label className="mb-1 block text-xs font-medium text-slate-600">Business Account ID</label><input className={inputCls} value={s.whatsapp?.businessAccountId || ""} onChange={(e) => setS({ ...s, whatsapp: { ...s.whatsapp, businessAccountId: e.target.value } })} /></div>
            <div><label className="mb-1 block text-xs font-medium text-slate-600">Phone Number ID</label><input className={inputCls} value={s.whatsapp?.phoneNumberId || ""} onChange={(e) => setS({ ...s, whatsapp: { ...s.whatsapp, phoneNumberId: e.target.value } })} /></div>
            <div><label className="mb-1 block text-xs font-medium text-slate-600">Access Token</label><input className={inputCls} type="password" value={s.whatsapp?.accessToken || ""} onChange={(e) => setS({ ...s, whatsapp: { ...s.whatsapp, accessToken: e.target.value } })} /></div>
          </div>
        </div>

        <div className="card-surface p-6">
          <h3 className="mb-4 flex items-center gap-2 font-heading text-base font-semibold text-slate-800"><Mail className="h-5 w-5 text-brand-600" /> SMTP Email</h3>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="mb-1 block text-xs font-medium text-slate-600">Host</label><input className={inputCls} value={s.smtp?.host || ""} onChange={(e) => setS({ ...s, smtp: { ...s.smtp, host: e.target.value } })} /></div>
            <div><label className="mb-1 block text-xs font-medium text-slate-600">Port</label><input className={inputCls} value={s.smtp?.port || ""} onChange={(e) => setS({ ...s, smtp: { ...s.smtp, port: e.target.value } })} /></div>
            <div><label className="mb-1 block text-xs font-medium text-slate-600">User</label><input className={inputCls} value={s.smtp?.user || ""} onChange={(e) => setS({ ...s, smtp: { ...s.smtp, user: e.target.value } })} /></div>
            <div><label className="mb-1 block text-xs font-medium text-slate-600">Password</label><input type="password" className={inputCls} value={s.smtp?.pass || ""} onChange={(e) => setS({ ...s, smtp: { ...s.smtp, pass: e.target.value } })} /></div>
          </div>
        </div>

        <div className="card-surface p-6">
          <h3 className="mb-4 font-heading text-base font-semibold text-slate-800">Security</h3>
          <p className="text-sm text-slate-500">Update your super admin password.</p>
          <Button variant="outline" className="mt-4 rounded-xl" onClick={() => navigate("/change-password")} data-testid="admin-change-password-btn"><KeyRound className="mr-1 h-4 w-4" /> Change Password</Button>
        </div>
      </div>
    </div>
  );
}
